import * as z from 'zod';

export const CreateCustomizationRequest = z.object({
  customization: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Customization ID.')
          .min(1)
          .max(36)
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the customization is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the customization.\n\nIgnored when creating a customization.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the customization was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the customization was updated.')
          .optional()
          .nullable(),
        key: z
          .string()
          .describe(
            'A read-only identifier generated from the customization name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
          )
          .min(1)
          .max(50)
          .optional()
          .nullable(),
        name: z
          .string()
          .describe(
            'Customization name for options (for example, `"color"`, `"size"`) and modifiers (for example, `"greeting card"`).'
          )
          .min(1)
          .max(50),
        customizationType: z.enum(['PRODUCT_OPTION', 'MODIFIER']),
        customizationRenderType: z.enum([
          'FREE_TEXT',
          'TEXT_CHOICES',
          'SWATCH_CHOICES',
        ]),
        assignedProductsCount: z
          .number()
          .int()
          .describe(
            'Number of products this customization is assigned to.\n> **Note:** Returned only when you pass `"ASSIGNED_PRODUCTS_COUNT"` to the `fields` array in Customizations API requests.'
          )
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          choicesSettings: z.never().optional(),
          freeTextInput: z
            .object({
              minCharCount: z
                .number()
                .int()
                .describe('Minimum text character length.')
                .optional(),
              maxCharCount: z
                .number()
                .int()
                .describe('Maximum text character length.')
                .max(500)
                .optional(),
              defaultAddedPrice: z
                .string()
                .describe(
                  "Default amount added to a product's price when this choice is assigned to a modifier."
                )
                .optional()
                .nullable(),
              title: z
                .string()
                .describe(
                  'Title to display to customer for their free-text input.'
                )
                .min(1)
                .max(100),
              key: z
                .string()
                .describe(
                  'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                )
                .min(1)
                .max(150)
                .optional()
                .nullable(),
            })
            .describe(
              'Free text input settings.\n\n> **Note:** To be passed along with `customizationRenderType: FREE_TEXT`.'
            ),
        }),
        z.object({
          freeTextInput: z.never().optional(),
          choicesSettings: z
            .object({
              choices: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe('Choice ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      choiceType: z
                        .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Choice name.')
                        .min(1)
                        .max(50)
                        .optional(),
                      defaultAddedPrice: z
                        .string()
                        .describe(
                          "Default amount added to a product's price when this customization is assigned to a modifier."
                        )
                        .optional()
                        .nullable(),
                    }),
                    z.xor([
                      z.object({ colorCode: z.never().optional() }),
                      z.object({
                        colorCode: z
                          .string()
                          .describe(
                            'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                          )
                          .min(3)
                          .max(20),
                      }),
                    ])
                  )
                )
                .min(1)
                .max(200),
            })
            .describe(
              'Choices settings.\n\n> **Note:** Must be passed along with `customizationRenderType` of `SWATCH_CHOICES` and `TEXT_CHOICES`.'
            ),
        }),
      ])
    )
    .describe('Customization to create.'),
});
export const CreateCustomizationResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Customization ID.')
      .min(1)
      .max(36)
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'Revision number, which increments by 1 each time the customization is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the customization.\n\nIgnored when creating a customization.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the customization was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the customization was updated.')
      .optional()
      .nullable(),
    key: z
      .string()
      .describe(
        'A read-only identifier generated from the customization name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
      )
      .min(1)
      .max(50)
      .optional()
      .nullable(),
    name: z
      .string()
      .describe(
        'Customization name for options (for example, `"color"`, `"size"`) and modifiers (for example, `"greeting card"`).'
      )
      .min(1)
      .max(50)
      .optional(),
    customizationType: z
      .enum(['PRODUCT_OPTION', 'MODIFIER'])
      .describe('Customization type.')
      .optional(),
    customizationRenderType: z
      .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
      .describe(
        'Customization render type.\n\nDefines how the customization will be displayed in the storefront.'
      )
      .optional(),
    assignedProductsCount: z
      .number()
      .int()
      .describe(
        'Number of products this customization is assigned to.\n> **Note:** Returned only when you pass `"ASSIGNED_PRODUCTS_COUNT"` to the `fields` array in Customizations API requests.'
      )
      .optional()
      .nullable(),
  }),
  z.xor([
    z.object({
      freeTextInput: z.never().optional(),
      choicesSettings: z.never().optional(),
    }),
    z.object({
      choicesSettings: z.never().optional(),
      freeTextInput: z
        .object({
          minCharCount: z
            .number()
            .int()
            .describe('Minimum text character length.')
            .optional(),
          maxCharCount: z
            .number()
            .int()
            .describe('Maximum text character length.')
            .max(500)
            .optional(),
          defaultAddedPrice: z
            .string()
            .describe(
              "Default amount added to a product's price when this choice is assigned to a modifier."
            )
            .optional()
            .nullable(),
          title: z
            .string()
            .describe('Title to display to customer for their free-text input.')
            .min(1)
            .max(100)
            .optional(),
          key: z
            .string()
            .describe(
              'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
            )
            .min(1)
            .max(150)
            .optional()
            .nullable(),
        })
        .describe(
          'Free text input settings.\n\n> **Note:** To be passed along with `customizationRenderType: FREE_TEXT`.'
        ),
    }),
    z.object({
      freeTextInput: z.never().optional(),
      choicesSettings: z
        .object({
          choices: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Choice ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  choiceType: z
                    .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                    .describe(
                      'Choice type.\n\n> **Notes:**\n> + For `customizationRenderType: SWATCH_CHOICES`, the supported `choiceType` value is: `ONE_COLOR`.\n> + For a `customizationRenderType` of `TEXT_CHOICES`, the supported `choiceType` value is: `CHOICE_TEXT`.'
                    )
                    .optional(),
                  key: z
                    .string()
                    .describe(
                      'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                    )
                    .min(1)
                    .max(50)
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Choice name.')
                    .min(1)
                    .max(50)
                    .optional(),
                  defaultAddedPrice: z
                    .string()
                    .describe(
                      "Default amount added to a product's price when this customization is assigned to a modifier."
                    )
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({ colorCode: z.never().optional() }),
                  z.object({
                    colorCode: z
                      .string()
                      .describe(
                        'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                      )
                      .min(3)
                      .max(20),
                  }),
                ])
              )
            )
            .min(1)
            .max(200)
            .optional(),
        })
        .describe(
          'Choices settings.\n\n> **Note:** Must be passed along with `customizationRenderType` of `SWATCH_CHOICES` and `TEXT_CHOICES`.'
        ),
    }),
  ])
);
export const GetCustomizationRequest = z.object({
  customizationId: z
    .string()
    .describe('Customization ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      fields: z
        .array(z.enum(['ASSIGNED_PRODUCTS_COUNT']))
        .max(100)
        .optional(),
    })
    .optional(),
});
export const GetCustomizationResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Customization ID.')
      .min(1)
      .max(36)
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'Revision number, which increments by 1 each time the customization is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the customization.\n\nIgnored when creating a customization.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the customization was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the customization was updated.')
      .optional()
      .nullable(),
    key: z
      .string()
      .describe(
        'A read-only identifier generated from the customization name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
      )
      .min(1)
      .max(50)
      .optional()
      .nullable(),
    name: z
      .string()
      .describe(
        'Customization name for options (for example, `"color"`, `"size"`) and modifiers (for example, `"greeting card"`).'
      )
      .min(1)
      .max(50)
      .optional(),
    customizationType: z
      .enum(['PRODUCT_OPTION', 'MODIFIER'])
      .describe('Customization type.')
      .optional(),
    customizationRenderType: z
      .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
      .describe(
        'Customization render type.\n\nDefines how the customization will be displayed in the storefront.'
      )
      .optional(),
    assignedProductsCount: z
      .number()
      .int()
      .describe(
        'Number of products this customization is assigned to.\n> **Note:** Returned only when you pass `"ASSIGNED_PRODUCTS_COUNT"` to the `fields` array in Customizations API requests.'
      )
      .optional()
      .nullable(),
  }),
  z.xor([
    z.object({
      freeTextInput: z.never().optional(),
      choicesSettings: z.never().optional(),
    }),
    z.object({
      choicesSettings: z.never().optional(),
      freeTextInput: z
        .object({
          minCharCount: z
            .number()
            .int()
            .describe('Minimum text character length.')
            .optional(),
          maxCharCount: z
            .number()
            .int()
            .describe('Maximum text character length.')
            .max(500)
            .optional(),
          defaultAddedPrice: z
            .string()
            .describe(
              "Default amount added to a product's price when this choice is assigned to a modifier."
            )
            .optional()
            .nullable(),
          title: z
            .string()
            .describe('Title to display to customer for their free-text input.')
            .min(1)
            .max(100)
            .optional(),
          key: z
            .string()
            .describe(
              'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
            )
            .min(1)
            .max(150)
            .optional()
            .nullable(),
        })
        .describe(
          'Free text input settings.\n\n> **Note:** To be passed along with `customizationRenderType: FREE_TEXT`.'
        ),
    }),
    z.object({
      freeTextInput: z.never().optional(),
      choicesSettings: z
        .object({
          choices: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Choice ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  choiceType: z
                    .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                    .describe(
                      'Choice type.\n\n> **Notes:**\n> + For `customizationRenderType: SWATCH_CHOICES`, the supported `choiceType` value is: `ONE_COLOR`.\n> + For a `customizationRenderType` of `TEXT_CHOICES`, the supported `choiceType` value is: `CHOICE_TEXT`.'
                    )
                    .optional(),
                  key: z
                    .string()
                    .describe(
                      'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                    )
                    .min(1)
                    .max(50)
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Choice name.')
                    .min(1)
                    .max(50)
                    .optional(),
                  defaultAddedPrice: z
                    .string()
                    .describe(
                      "Default amount added to a product's price when this customization is assigned to a modifier."
                    )
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({ colorCode: z.never().optional() }),
                  z.object({
                    colorCode: z
                      .string()
                      .describe(
                        'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                      )
                      .min(3)
                      .max(20),
                  }),
                ])
              )
            )
            .min(1)
            .max(200)
            .optional(),
        })
        .describe(
          'Choices settings.\n\n> **Note:** Must be passed along with `customizationRenderType` of `SWATCH_CHOICES` and `TEXT_CHOICES`.'
        ),
    }),
  ])
);
export const UpdateCustomizationRequest = z.object({
  _id: z.string().describe('Customization ID.').min(1).max(36),
  customization: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Customization ID.')
          .min(1)
          .max(36)
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the customization is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the customization.\n\nIgnored when creating a customization.'
          ),
        _createdDate: z
          .date()
          .describe('Date and time the customization was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the customization was updated.')
          .optional()
          .nullable(),
        key: z
          .string()
          .describe(
            'A read-only identifier generated from the customization name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
          )
          .min(1)
          .max(50)
          .optional()
          .nullable(),
        name: z
          .string()
          .describe(
            'Customization name for options (for example, `"color"`, `"size"`) and modifiers (for example, `"greeting card"`).'
          )
          .min(1)
          .max(50)
          .optional(),
        customizationType: z.enum(['PRODUCT_OPTION', 'MODIFIER']).optional(),
        customizationRenderType: z
          .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
          .optional(),
        assignedProductsCount: z
          .number()
          .int()
          .describe(
            'Number of products this customization is assigned to.\n> **Note:** Returned only when you pass `"ASSIGNED_PRODUCTS_COUNT"` to the `fields` array in Customizations API requests.'
          )
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          freeTextInput: z.never().optional(),
          choicesSettings: z.never().optional(),
        }),
        z.object({
          choicesSettings: z.never().optional(),
          freeTextInput: z
            .object({
              minCharCount: z
                .number()
                .int()
                .describe('Minimum text character length.')
                .optional(),
              maxCharCount: z
                .number()
                .int()
                .describe('Maximum text character length.')
                .max(500)
                .optional(),
              defaultAddedPrice: z
                .string()
                .describe(
                  "Default amount added to a product's price when this choice is assigned to a modifier."
                )
                .optional()
                .nullable(),
              title: z
                .string()
                .describe(
                  'Title to display to customer for their free-text input.'
                )
                .min(1)
                .max(100)
                .optional(),
              key: z
                .string()
                .describe(
                  'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                )
                .min(1)
                .max(150)
                .optional()
                .nullable(),
            })
            .describe(
              'Free text input settings.\n\n> **Note:** To be passed along with `customizationRenderType: FREE_TEXT`.'
            ),
        }),
        z.object({
          freeTextInput: z.never().optional(),
          choicesSettings: z
            .object({
              choices: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe('Choice ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        ),
                      choiceType: z
                        .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Choice name.')
                        .min(1)
                        .max(50)
                        .optional(),
                      defaultAddedPrice: z
                        .string()
                        .describe(
                          "Default amount added to a product's price when this customization is assigned to a modifier."
                        )
                        .optional()
                        .nullable(),
                    }),
                    z.xor([
                      z.object({ colorCode: z.never().optional() }),
                      z.object({
                        colorCode: z
                          .string()
                          .describe(
                            'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                          )
                          .min(3)
                          .max(20),
                      }),
                    ])
                  )
                )
                .min(1)
                .max(200)
                .optional(),
            })
            .describe(
              'Choices settings.\n\n> **Note:** Must be passed along with `customizationRenderType` of `SWATCH_CHOICES` and `TEXT_CHOICES`.'
            ),
        }),
      ])
    )
    .describe('Customization to update.'),
  options: z
    .object({
      fields: z
        .array(z.enum(['ASSIGNED_PRODUCTS_COUNT']))
        .max(100)
        .optional(),
    })
    .optional(),
});
export const UpdateCustomizationResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Customization ID.')
      .min(1)
      .max(36)
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'Revision number, which increments by 1 each time the customization is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the customization.\n\nIgnored when creating a customization.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the customization was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the customization was updated.')
      .optional()
      .nullable(),
    key: z
      .string()
      .describe(
        'A read-only identifier generated from the customization name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
      )
      .min(1)
      .max(50)
      .optional()
      .nullable(),
    name: z
      .string()
      .describe(
        'Customization name for options (for example, `"color"`, `"size"`) and modifiers (for example, `"greeting card"`).'
      )
      .min(1)
      .max(50)
      .optional(),
    customizationType: z
      .enum(['PRODUCT_OPTION', 'MODIFIER'])
      .describe('Customization type.')
      .optional(),
    customizationRenderType: z
      .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
      .describe(
        'Customization render type.\n\nDefines how the customization will be displayed in the storefront.'
      )
      .optional(),
    assignedProductsCount: z
      .number()
      .int()
      .describe(
        'Number of products this customization is assigned to.\n> **Note:** Returned only when you pass `"ASSIGNED_PRODUCTS_COUNT"` to the `fields` array in Customizations API requests.'
      )
      .optional()
      .nullable(),
  }),
  z.xor([
    z.object({
      freeTextInput: z.never().optional(),
      choicesSettings: z.never().optional(),
    }),
    z.object({
      choicesSettings: z.never().optional(),
      freeTextInput: z
        .object({
          minCharCount: z
            .number()
            .int()
            .describe('Minimum text character length.')
            .optional(),
          maxCharCount: z
            .number()
            .int()
            .describe('Maximum text character length.')
            .max(500)
            .optional(),
          defaultAddedPrice: z
            .string()
            .describe(
              "Default amount added to a product's price when this choice is assigned to a modifier."
            )
            .optional()
            .nullable(),
          title: z
            .string()
            .describe('Title to display to customer for their free-text input.')
            .min(1)
            .max(100)
            .optional(),
          key: z
            .string()
            .describe(
              'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
            )
            .min(1)
            .max(150)
            .optional()
            .nullable(),
        })
        .describe(
          'Free text input settings.\n\n> **Note:** To be passed along with `customizationRenderType: FREE_TEXT`.'
        ),
    }),
    z.object({
      freeTextInput: z.never().optional(),
      choicesSettings: z
        .object({
          choices: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Choice ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  choiceType: z
                    .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                    .describe(
                      'Choice type.\n\n> **Notes:**\n> + For `customizationRenderType: SWATCH_CHOICES`, the supported `choiceType` value is: `ONE_COLOR`.\n> + For a `customizationRenderType` of `TEXT_CHOICES`, the supported `choiceType` value is: `CHOICE_TEXT`.'
                    )
                    .optional(),
                  key: z
                    .string()
                    .describe(
                      'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                    )
                    .min(1)
                    .max(50)
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Choice name.')
                    .min(1)
                    .max(50)
                    .optional(),
                  defaultAddedPrice: z
                    .string()
                    .describe(
                      "Default amount added to a product's price when this customization is assigned to a modifier."
                    )
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({ colorCode: z.never().optional() }),
                  z.object({
                    colorCode: z
                      .string()
                      .describe(
                        'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                      )
                      .min(3)
                      .max(20),
                  }),
                ])
              )
            )
            .min(1)
            .max(200)
            .optional(),
        })
        .describe(
          'Choices settings.\n\n> **Note:** Must be passed along with `customizationRenderType` of `SWATCH_CHOICES` and `TEXT_CHOICES`.'
        ),
    }),
  ])
);
export const DeleteCustomizationRequest = z.object({
  customizationId: z
    .string()
    .describe('Customization ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteCustomizationResponse = z.object({});
export const QueryCustomizationsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _updatedDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          name: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          customizationType: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          customizationRenderType: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum(['_id', '_createdDate', '_updatedDate', 'name'])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Query options.'),
  options: z
    .object({
      fields: z
        .array(z.enum(['ASSIGNED_PRODUCTS_COUNT']))
        .max(100)
        .optional(),
    })
    .optional(),
});
export const QueryCustomizationsResponse = z.object({
  customizations: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Customization ID.')
            .min(1)
            .max(36)
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the customization is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the customization.\n\nIgnored when creating a customization.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the customization was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the customization was updated.')
            .optional()
            .nullable(),
          key: z
            .string()
            .describe(
              'A read-only identifier generated from the customization name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
            )
            .min(1)
            .max(50)
            .optional()
            .nullable(),
          name: z
            .string()
            .describe(
              'Customization name for options (for example, `"color"`, `"size"`) and modifiers (for example, `"greeting card"`).'
            )
            .min(1)
            .max(50)
            .optional(),
          customizationType: z
            .enum(['PRODUCT_OPTION', 'MODIFIER'])
            .describe('Customization type.')
            .optional(),
          customizationRenderType: z
            .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
            .describe(
              'Customization render type.\n\nDefines how the customization will be displayed in the storefront.'
            )
            .optional(),
          assignedProductsCount: z
            .number()
            .int()
            .describe(
              'Number of products this customization is assigned to.\n> **Note:** Returned only when you pass `"ASSIGNED_PRODUCTS_COUNT"` to the `fields` array in Customizations API requests.'
            )
            .optional()
            .nullable(),
        }),
        z.xor([
          z.object({
            freeTextInput: z.never().optional(),
            choicesSettings: z.never().optional(),
          }),
          z.object({
            choicesSettings: z.never().optional(),
            freeTextInput: z
              .object({
                minCharCount: z
                  .number()
                  .int()
                  .describe('Minimum text character length.')
                  .optional(),
                maxCharCount: z
                  .number()
                  .int()
                  .describe('Maximum text character length.')
                  .max(500)
                  .optional(),
                defaultAddedPrice: z
                  .string()
                  .describe(
                    "Default amount added to a product's price when this choice is assigned to a modifier."
                  )
                  .optional()
                  .nullable(),
                title: z
                  .string()
                  .describe(
                    'Title to display to customer for their free-text input.'
                  )
                  .min(1)
                  .max(100)
                  .optional(),
                key: z
                  .string()
                  .describe(
                    'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                  )
                  .min(1)
                  .max(150)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Free text input settings.\n\n> **Note:** To be passed along with `customizationRenderType: FREE_TEXT`.'
              ),
          }),
          z.object({
            freeTextInput: z.never().optional(),
            choicesSettings: z
              .object({
                choices: z
                  .array(
                    z.intersection(
                      z.object({
                        _id: z
                          .string()
                          .describe('Choice ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        choiceType: z
                          .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                          .describe(
                            'Choice type.\n\n> **Notes:**\n> + For `customizationRenderType: SWATCH_CHOICES`, the supported `choiceType` value is: `ONE_COLOR`.\n> + For a `customizationRenderType` of `TEXT_CHOICES`, the supported `choiceType` value is: `CHOICE_TEXT`.'
                          )
                          .optional(),
                        key: z
                          .string()
                          .describe(
                            'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                          )
                          .min(1)
                          .max(50)
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Choice name.')
                          .min(1)
                          .max(50)
                          .optional(),
                        defaultAddedPrice: z
                          .string()
                          .describe(
                            "Default amount added to a product's price when this customization is assigned to a modifier."
                          )
                          .optional()
                          .nullable(),
                      }),
                      z.xor([
                        z.object({ colorCode: z.never().optional() }),
                        z.object({
                          colorCode: z
                            .string()
                            .describe(
                              'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                            )
                            .min(3)
                            .max(20),
                        }),
                      ])
                    )
                  )
                  .min(1)
                  .max(200)
                  .optional(),
              })
              .describe(
                'Choices settings.\n\n> **Note:** Must be passed along with `customizationRenderType` of `SWATCH_CHOICES` and `TEXT_CHOICES`.'
              ),
          }),
        ])
      )
    )
    .min(1)
    .max(200)
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Details on the paged set of results returned.')
    .optional(),
});
export const BulkCreateCustomizationsRequest = z.object({
  customizations: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Customization ID.')
            .min(1)
            .max(36)
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the customization is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the customization.\n\nIgnored when creating a customization.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the customization was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the customization was updated.')
            .optional()
            .nullable(),
          key: z
            .string()
            .describe(
              'A read-only identifier generated from the customization name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
            )
            .min(1)
            .max(50)
            .optional()
            .nullable(),
          name: z
            .string()
            .describe(
              'Customization name for options (for example, `"color"`, `"size"`) and modifiers (for example, `"greeting card"`).'
            )
            .min(1)
            .max(50)
            .optional(),
          customizationType: z.enum(['PRODUCT_OPTION', 'MODIFIER']).optional(),
          customizationRenderType: z
            .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
            .optional(),
          assignedProductsCount: z
            .number()
            .int()
            .describe(
              'Number of products this customization is assigned to.\n> **Note:** Returned only when you pass `"ASSIGNED_PRODUCTS_COUNT"` to the `fields` array in Customizations API requests.'
            )
            .optional()
            .nullable(),
        }),
        z.xor([
          z.object({
            freeTextInput: z.never().optional(),
            choicesSettings: z.never().optional(),
          }),
          z.object({
            choicesSettings: z.never().optional(),
            freeTextInput: z
              .object({
                minCharCount: z
                  .number()
                  .int()
                  .describe('Minimum text character length.')
                  .optional(),
                maxCharCount: z
                  .number()
                  .int()
                  .describe('Maximum text character length.')
                  .max(500)
                  .optional(),
                defaultAddedPrice: z
                  .string()
                  .describe(
                    "Default amount added to a product's price when this choice is assigned to a modifier."
                  )
                  .optional()
                  .nullable(),
                title: z
                  .string()
                  .describe(
                    'Title to display to customer for their free-text input.'
                  )
                  .min(1)
                  .max(100)
                  .optional(),
                key: z
                  .string()
                  .describe(
                    'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                  )
                  .min(1)
                  .max(150)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Free text input settings.\n\n> **Note:** To be passed along with `customizationRenderType: FREE_TEXT`.'
              ),
          }),
          z.object({
            freeTextInput: z.never().optional(),
            choicesSettings: z
              .object({
                choices: z
                  .array(
                    z.intersection(
                      z.object({
                        _id: z
                          .string()
                          .describe('Choice ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        choiceType: z
                          .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                          .optional(),
                        key: z
                          .string()
                          .describe(
                            'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                          )
                          .min(1)
                          .max(50)
                          .optional()
                          .nullable(),
                        name: z
                          .string()
                          .describe('Choice name.')
                          .min(1)
                          .max(50)
                          .optional(),
                        defaultAddedPrice: z
                          .string()
                          .describe(
                            "Default amount added to a product's price when this customization is assigned to a modifier."
                          )
                          .optional()
                          .nullable(),
                      }),
                      z.xor([
                        z.object({ colorCode: z.never().optional() }),
                        z.object({
                          colorCode: z
                            .string()
                            .describe(
                              'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                            )
                            .min(3)
                            .max(20),
                        }),
                      ])
                    )
                  )
                  .min(1)
                  .max(200)
                  .optional(),
              })
              .describe(
                'Choices settings.\n\n> **Note:** Must be passed along with `customizationRenderType` of `SWATCH_CHOICES` and `TEXT_CHOICES`.'
              ),
          }),
        ])
      )
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether to return the full customization entities in the response.\n\nDefault: `false`'
        )
        .optional(),
    })
    .optional(),
});
export const BulkCreateCustomizationsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Bulk action metadata for customization.')
          .optional(),
        customization: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Customization ID.')
                .min(1)
                .max(36)
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the customization is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the customization.\n\nIgnored when creating a customization.'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe('Date and time the customization was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the customization was updated.')
                .optional()
                .nullable(),
              key: z
                .string()
                .describe(
                  'A read-only identifier generated from the customization name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                )
                .min(1)
                .max(50)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe(
                  'Customization name for options (for example, `"color"`, `"size"`) and modifiers (for example, `"greeting card"`).'
                )
                .min(1)
                .max(50)
                .optional(),
              customizationType: z
                .enum(['PRODUCT_OPTION', 'MODIFIER'])
                .describe('Customization type.')
                .optional(),
              customizationRenderType: z
                .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
                .describe(
                  'Customization render type.\n\nDefines how the customization will be displayed in the storefront.'
                )
                .optional(),
              assignedProductsCount: z
                .number()
                .int()
                .describe(
                  'Number of products this customization is assigned to.\n> **Note:** Returned only when you pass `"ASSIGNED_PRODUCTS_COUNT"` to the `fields` array in Customizations API requests.'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                freeTextInput: z.never().optional(),
                choicesSettings: z.never().optional(),
              }),
              z.object({
                choicesSettings: z.never().optional(),
                freeTextInput: z
                  .object({
                    minCharCount: z
                      .number()
                      .int()
                      .describe('Minimum text character length.')
                      .optional(),
                    maxCharCount: z
                      .number()
                      .int()
                      .describe('Maximum text character length.')
                      .max(500)
                      .optional(),
                    defaultAddedPrice: z
                      .string()
                      .describe(
                        "Default amount added to a product's price when this choice is assigned to a modifier."
                      )
                      .optional()
                      .nullable(),
                    title: z
                      .string()
                      .describe(
                        'Title to display to customer for their free-text input.'
                      )
                      .min(1)
                      .max(100)
                      .optional(),
                    key: z
                      .string()
                      .describe(
                        'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                      )
                      .min(1)
                      .max(150)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Free text input settings.\n\n> **Note:** To be passed along with `customizationRenderType: FREE_TEXT`.'
                  ),
              }),
              z.object({
                freeTextInput: z.never().optional(),
                choicesSettings: z
                  .object({
                    choices: z
                      .array(
                        z.intersection(
                          z.object({
                            _id: z
                              .string()
                              .describe('Choice ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            choiceType: z
                              .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                              .describe(
                                'Choice type.\n\n> **Notes:**\n> + For `customizationRenderType: SWATCH_CHOICES`, the supported `choiceType` value is: `ONE_COLOR`.\n> + For a `customizationRenderType` of `TEXT_CHOICES`, the supported `choiceType` value is: `CHOICE_TEXT`.'
                              )
                              .optional(),
                            key: z
                              .string()
                              .describe(
                                'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                              )
                              .min(1)
                              .max(50)
                              .optional()
                              .nullable(),
                            name: z
                              .string()
                              .describe('Choice name.')
                              .min(1)
                              .max(50)
                              .optional(),
                            defaultAddedPrice: z
                              .string()
                              .describe(
                                "Default amount added to a product's price when this customization is assigned to a modifier."
                              )
                              .optional()
                              .nullable(),
                          }),
                          z.xor([
                            z.object({ colorCode: z.never().optional() }),
                            z.object({
                              colorCode: z
                                .string()
                                .describe(
                                  'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                )
                                .min(3)
                                .max(20),
                            }),
                          ])
                        )
                      )
                      .min(1)
                      .max(200)
                      .optional(),
                  })
                  .describe(
                    'Choices settings.\n\n> **Note:** Must be passed along with `customizationRenderType` of `SWATCH_CHOICES` and `TEXT_CHOICES`.'
                  ),
              }),
            ])
          )
          .describe(
            'Full customization entity.\n\nReturned only if `returnEntity: true` is passed in the request.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const AddCustomizationChoicesRequest = z.object({
  customizationId: z
    .string()
    .describe('Customization ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  choices: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Choice ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          choiceType: z.enum(['CHOICE_TEXT', 'ONE_COLOR']).optional(),
          key: z
            .string()
            .describe(
              'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
            )
            .min(1)
            .max(50)
            .optional()
            .nullable(),
          name: z.string().describe('Choice name.').min(1).max(50).optional(),
          defaultAddedPrice: z
            .string()
            .describe(
              "Default amount added to a product's price when this customization is assigned to a modifier."
            )
            .optional()
            .nullable(),
        }),
        z.xor([
          z.object({ colorCode: z.never().optional() }),
          z.object({
            colorCode: z
              .string()
              .describe(
                'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
              )
              .min(3)
              .max(20),
          }),
        ])
      )
    )
    .min(1)
    .max(100),
  options: z
    .object({
      fields: z
        .array(z.enum(['ASSIGNED_PRODUCTS_COUNT']))
        .max(100)
        .optional(),
    })
    .optional(),
});
export const AddCustomizationChoicesResponse = z.object({
  customization: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Customization ID.')
          .min(1)
          .max(36)
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the customization is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the customization.\n\nIgnored when creating a customization.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the customization was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the customization was updated.')
          .optional()
          .nullable(),
        key: z
          .string()
          .describe(
            'A read-only identifier generated from the customization name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
          )
          .min(1)
          .max(50)
          .optional()
          .nullable(),
        name: z
          .string()
          .describe(
            'Customization name for options (for example, `"color"`, `"size"`) and modifiers (for example, `"greeting card"`).'
          )
          .min(1)
          .max(50)
          .optional(),
        customizationType: z
          .enum(['PRODUCT_OPTION', 'MODIFIER'])
          .describe('Customization type.')
          .optional(),
        customizationRenderType: z
          .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
          .describe(
            'Customization render type.\n\nDefines how the customization will be displayed in the storefront.'
          )
          .optional(),
        assignedProductsCount: z
          .number()
          .int()
          .describe(
            'Number of products this customization is assigned to.\n> **Note:** Returned only when you pass `"ASSIGNED_PRODUCTS_COUNT"` to the `fields` array in Customizations API requests.'
          )
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          freeTextInput: z.never().optional(),
          choicesSettings: z.never().optional(),
        }),
        z.object({
          choicesSettings: z.never().optional(),
          freeTextInput: z
            .object({
              minCharCount: z
                .number()
                .int()
                .describe('Minimum text character length.')
                .optional(),
              maxCharCount: z
                .number()
                .int()
                .describe('Maximum text character length.')
                .max(500)
                .optional(),
              defaultAddedPrice: z
                .string()
                .describe(
                  "Default amount added to a product's price when this choice is assigned to a modifier."
                )
                .optional()
                .nullable(),
              title: z
                .string()
                .describe(
                  'Title to display to customer for their free-text input.'
                )
                .min(1)
                .max(100)
                .optional(),
              key: z
                .string()
                .describe(
                  'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                )
                .min(1)
                .max(150)
                .optional()
                .nullable(),
            })
            .describe(
              'Free text input settings.\n\n> **Note:** To be passed along with `customizationRenderType: FREE_TEXT`.'
            ),
        }),
        z.object({
          freeTextInput: z.never().optional(),
          choicesSettings: z
            .object({
              choices: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe('Choice ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      choiceType: z
                        .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                        .describe(
                          'Choice type.\n\n> **Notes:**\n> + For `customizationRenderType: SWATCH_CHOICES`, the supported `choiceType` value is: `ONE_COLOR`.\n> + For a `customizationRenderType` of `TEXT_CHOICES`, the supported `choiceType` value is: `CHOICE_TEXT`.'
                        )
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Choice name.')
                        .min(1)
                        .max(50)
                        .optional(),
                      defaultAddedPrice: z
                        .string()
                        .describe(
                          "Default amount added to a product's price when this customization is assigned to a modifier."
                        )
                        .optional()
                        .nullable(),
                    }),
                    z.xor([
                      z.object({ colorCode: z.never().optional() }),
                      z.object({
                        colorCode: z
                          .string()
                          .describe(
                            'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                          )
                          .min(3)
                          .max(20),
                      }),
                    ])
                  )
                )
                .min(1)
                .max(200)
                .optional(),
            })
            .describe(
              'Choices settings.\n\n> **Note:** Must be passed along with `customizationRenderType` of `SWATCH_CHOICES` and `TEXT_CHOICES`.'
            ),
        }),
      ])
    )
    .describe('Updated customization.')
    .optional(),
});
export const SetCustomizationChoicesRequest = z.object({
  customizationId: z
    .string()
    .describe('Customization ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  choices: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Choice ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          choiceType: z.enum(['CHOICE_TEXT', 'ONE_COLOR']).optional(),
          key: z
            .string()
            .describe(
              'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
            )
            .min(1)
            .max(50)
            .optional()
            .nullable(),
          name: z.string().describe('Choice name.').min(1).max(50).optional(),
          defaultAddedPrice: z
            .string()
            .describe(
              "Default amount added to a product's price when this customization is assigned to a modifier."
            )
            .optional()
            .nullable(),
        }),
        z.xor([
          z.object({ colorCode: z.never().optional() }),
          z.object({
            colorCode: z
              .string()
              .describe(
                'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
              )
              .min(3)
              .max(20),
          }),
        ])
      )
    )
    .min(1)
    .max(200),
  options: z
    .object({
      fields: z
        .array(z.enum(['ASSIGNED_PRODUCTS_COUNT']))
        .max(100)
        .optional(),
    })
    .optional(),
});
export const SetCustomizationChoicesResponse = z.object({
  customization: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Customization ID.')
          .min(1)
          .max(36)
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the customization is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the customization.\n\nIgnored when creating a customization.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the customization was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the customization was updated.')
          .optional()
          .nullable(),
        key: z
          .string()
          .describe(
            'A read-only identifier generated from the customization name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
          )
          .min(1)
          .max(50)
          .optional()
          .nullable(),
        name: z
          .string()
          .describe(
            'Customization name for options (for example, `"color"`, `"size"`) and modifiers (for example, `"greeting card"`).'
          )
          .min(1)
          .max(50)
          .optional(),
        customizationType: z
          .enum(['PRODUCT_OPTION', 'MODIFIER'])
          .describe('Customization type.')
          .optional(),
        customizationRenderType: z
          .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
          .describe(
            'Customization render type.\n\nDefines how the customization will be displayed in the storefront.'
          )
          .optional(),
        assignedProductsCount: z
          .number()
          .int()
          .describe(
            'Number of products this customization is assigned to.\n> **Note:** Returned only when you pass `"ASSIGNED_PRODUCTS_COUNT"` to the `fields` array in Customizations API requests.'
          )
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          freeTextInput: z.never().optional(),
          choicesSettings: z.never().optional(),
        }),
        z.object({
          choicesSettings: z.never().optional(),
          freeTextInput: z
            .object({
              minCharCount: z
                .number()
                .int()
                .describe('Minimum text character length.')
                .optional(),
              maxCharCount: z
                .number()
                .int()
                .describe('Maximum text character length.')
                .max(500)
                .optional(),
              defaultAddedPrice: z
                .string()
                .describe(
                  "Default amount added to a product's price when this choice is assigned to a modifier."
                )
                .optional()
                .nullable(),
              title: z
                .string()
                .describe(
                  'Title to display to customer for their free-text input.'
                )
                .min(1)
                .max(100)
                .optional(),
              key: z
                .string()
                .describe(
                  'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                )
                .min(1)
                .max(150)
                .optional()
                .nullable(),
            })
            .describe(
              'Free text input settings.\n\n> **Note:** To be passed along with `customizationRenderType: FREE_TEXT`.'
            ),
        }),
        z.object({
          freeTextInput: z.never().optional(),
          choicesSettings: z
            .object({
              choices: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe('Choice ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      choiceType: z
                        .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                        .describe(
                          'Choice type.\n\n> **Notes:**\n> + For `customizationRenderType: SWATCH_CHOICES`, the supported `choiceType` value is: `ONE_COLOR`.\n> + For a `customizationRenderType` of `TEXT_CHOICES`, the supported `choiceType` value is: `CHOICE_TEXT`.'
                        )
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Choice name.')
                        .min(1)
                        .max(50)
                        .optional(),
                      defaultAddedPrice: z
                        .string()
                        .describe(
                          "Default amount added to a product's price when this customization is assigned to a modifier."
                        )
                        .optional()
                        .nullable(),
                    }),
                    z.xor([
                      z.object({ colorCode: z.never().optional() }),
                      z.object({
                        colorCode: z
                          .string()
                          .describe(
                            'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                          )
                          .min(3)
                          .max(20),
                      }),
                    ])
                  )
                )
                .min(1)
                .max(200)
                .optional(),
            })
            .describe(
              'Choices settings.\n\n> **Note:** Must be passed along with `customizationRenderType` of `SWATCH_CHOICES` and `TEXT_CHOICES`.'
            ),
        }),
      ])
    )
    .describe('Updated customization.')
    .optional(),
});
export const RemoveCustomizationChoicesRequest = z.object({
  customizationId: z
    .string()
    .describe('Customization ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  choiceIds: z.array(z.string()).min(1).max(100),
  options: z
    .object({
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe('Customization revision.')
        .optional(),
      fields: z
        .array(z.enum(['ASSIGNED_PRODUCTS_COUNT']))
        .max(100)
        .optional(),
    })
    .optional(),
});
export const RemoveCustomizationChoicesResponse = z.object({
  customization: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Customization ID.')
          .min(1)
          .max(36)
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the customization is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the customization.\n\nIgnored when creating a customization.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the customization was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the customization was updated.')
          .optional()
          .nullable(),
        key: z
          .string()
          .describe(
            'A read-only identifier generated from the customization name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
          )
          .min(1)
          .max(50)
          .optional()
          .nullable(),
        name: z
          .string()
          .describe(
            'Customization name for options (for example, `"color"`, `"size"`) and modifiers (for example, `"greeting card"`).'
          )
          .min(1)
          .max(50)
          .optional(),
        customizationType: z
          .enum(['PRODUCT_OPTION', 'MODIFIER'])
          .describe('Customization type.')
          .optional(),
        customizationRenderType: z
          .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
          .describe(
            'Customization render type.\n\nDefines how the customization will be displayed in the storefront.'
          )
          .optional(),
        assignedProductsCount: z
          .number()
          .int()
          .describe(
            'Number of products this customization is assigned to.\n> **Note:** Returned only when you pass `"ASSIGNED_PRODUCTS_COUNT"` to the `fields` array in Customizations API requests.'
          )
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          freeTextInput: z.never().optional(),
          choicesSettings: z.never().optional(),
        }),
        z.object({
          choicesSettings: z.never().optional(),
          freeTextInput: z
            .object({
              minCharCount: z
                .number()
                .int()
                .describe('Minimum text character length.')
                .optional(),
              maxCharCount: z
                .number()
                .int()
                .describe('Maximum text character length.')
                .max(500)
                .optional(),
              defaultAddedPrice: z
                .string()
                .describe(
                  "Default amount added to a product's price when this choice is assigned to a modifier."
                )
                .optional()
                .nullable(),
              title: z
                .string()
                .describe(
                  'Title to display to customer for their free-text input.'
                )
                .min(1)
                .max(100)
                .optional(),
              key: z
                .string()
                .describe(
                  'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                )
                .min(1)
                .max(150)
                .optional()
                .nullable(),
            })
            .describe(
              'Free text input settings.\n\n> **Note:** To be passed along with `customizationRenderType: FREE_TEXT`.'
            ),
        }),
        z.object({
          freeTextInput: z.never().optional(),
          choicesSettings: z
            .object({
              choices: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe('Choice ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      choiceType: z
                        .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                        .describe(
                          'Choice type.\n\n> **Notes:**\n> + For `customizationRenderType: SWATCH_CHOICES`, the supported `choiceType` value is: `ONE_COLOR`.\n> + For a `customizationRenderType` of `TEXT_CHOICES`, the supported `choiceType` value is: `CHOICE_TEXT`.'
                        )
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Choice name.')
                        .min(1)
                        .max(50)
                        .optional(),
                      defaultAddedPrice: z
                        .string()
                        .describe(
                          "Default amount added to a product's price when this customization is assigned to a modifier."
                        )
                        .optional()
                        .nullable(),
                    }),
                    z.xor([
                      z.object({ colorCode: z.never().optional() }),
                      z.object({
                        colorCode: z
                          .string()
                          .describe(
                            'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                          )
                          .min(3)
                          .max(20),
                      }),
                    ])
                  )
                )
                .min(1)
                .max(200)
                .optional(),
            })
            .describe(
              'Choices settings.\n\n> **Note:** Must be passed along with `customizationRenderType` of `SWATCH_CHOICES` and `TEXT_CHOICES`.'
            ),
        }),
      ])
    )
    .describe('Updated customization.')
    .optional(),
});
export const BulkAddCustomizationChoicesRequest = z.object({
  customizationsChoices: z
    .array(
      z.object({
        customizationId: z
          .string()
          .describe('Customization ID.')
          .min(1)
          .max(36),
        choices: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe('Choice ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                choiceType: z.enum(['CHOICE_TEXT', 'ONE_COLOR']).optional(),
                key: z
                  .string()
                  .describe(
                    'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                  )
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Choice name.')
                  .min(1)
                  .max(50)
                  .optional(),
                defaultAddedPrice: z
                  .string()
                  .describe(
                    "Default amount added to a product's price when this customization is assigned to a modifier."
                  )
                  .optional()
                  .nullable(),
              }),
              z.xor([
                z.object({ colorCode: z.never().optional() }),
                z.object({
                  colorCode: z
                    .string()
                    .describe(
                      'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                    )
                    .min(3)
                    .max(20),
                }),
              ])
            )
          )
          .min(1)
          .max(100),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether to return the full customization entities in the response.\n\nDefault: `false`'
        )
        .optional(),
      fields: z
        .array(z.enum(['ASSIGNED_PRODUCTS_COUNT']))
        .max(100)
        .optional(),
    })
    .optional(),
});
export const BulkAddCustomizationChoicesResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Bulk action metadata for customization.')
          .optional(),
        customization: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Customization ID.')
                .min(1)
                .max(36)
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the customization is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the customization.\n\nIgnored when creating a customization.'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe('Date and time the customization was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the customization was updated.')
                .optional()
                .nullable(),
              key: z
                .string()
                .describe(
                  'A read-only identifier generated from the customization name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                )
                .min(1)
                .max(50)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe(
                  'Customization name for options (for example, `"color"`, `"size"`) and modifiers (for example, `"greeting card"`).'
                )
                .min(1)
                .max(50)
                .optional(),
              customizationType: z
                .enum(['PRODUCT_OPTION', 'MODIFIER'])
                .describe('Customization type.')
                .optional(),
              customizationRenderType: z
                .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
                .describe(
                  'Customization render type.\n\nDefines how the customization will be displayed in the storefront.'
                )
                .optional(),
              assignedProductsCount: z
                .number()
                .int()
                .describe(
                  'Number of products this customization is assigned to.\n> **Note:** Returned only when you pass `"ASSIGNED_PRODUCTS_COUNT"` to the `fields` array in Customizations API requests.'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                freeTextInput: z.never().optional(),
                choicesSettings: z.never().optional(),
              }),
              z.object({
                choicesSettings: z.never().optional(),
                freeTextInput: z
                  .object({
                    minCharCount: z
                      .number()
                      .int()
                      .describe('Minimum text character length.')
                      .optional(),
                    maxCharCount: z
                      .number()
                      .int()
                      .describe('Maximum text character length.')
                      .max(500)
                      .optional(),
                    defaultAddedPrice: z
                      .string()
                      .describe(
                        "Default amount added to a product's price when this choice is assigned to a modifier."
                      )
                      .optional()
                      .nullable(),
                    title: z
                      .string()
                      .describe(
                        'Title to display to customer for their free-text input.'
                      )
                      .min(1)
                      .max(100)
                      .optional(),
                    key: z
                      .string()
                      .describe(
                        'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                      )
                      .min(1)
                      .max(150)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Free text input settings.\n\n> **Note:** To be passed along with `customizationRenderType: FREE_TEXT`.'
                  ),
              }),
              z.object({
                freeTextInput: z.never().optional(),
                choicesSettings: z
                  .object({
                    choices: z
                      .array(
                        z.intersection(
                          z.object({
                            _id: z
                              .string()
                              .describe('Choice ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            choiceType: z
                              .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                              .describe(
                                'Choice type.\n\n> **Notes:**\n> + For `customizationRenderType: SWATCH_CHOICES`, the supported `choiceType` value is: `ONE_COLOR`.\n> + For a `customizationRenderType` of `TEXT_CHOICES`, the supported `choiceType` value is: `CHOICE_TEXT`.'
                              )
                              .optional(),
                            key: z
                              .string()
                              .describe(
                                'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                              )
                              .min(1)
                              .max(50)
                              .optional()
                              .nullable(),
                            name: z
                              .string()
                              .describe('Choice name.')
                              .min(1)
                              .max(50)
                              .optional(),
                            defaultAddedPrice: z
                              .string()
                              .describe(
                                "Default amount added to a product's price when this customization is assigned to a modifier."
                              )
                              .optional()
                              .nullable(),
                          }),
                          z.xor([
                            z.object({ colorCode: z.never().optional() }),
                            z.object({
                              colorCode: z
                                .string()
                                .describe(
                                  'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                )
                                .min(3)
                                .max(20),
                            }),
                          ])
                        )
                      )
                      .min(1)
                      .max(200)
                      .optional(),
                  })
                  .describe(
                    'Choices settings.\n\n> **Note:** Must be passed along with `customizationRenderType` of `SWATCH_CHOICES` and `TEXT_CHOICES`.'
                  ),
              }),
            ])
          )
          .describe(
            'Full customization entity.\n\nReturned only if `returnEntity: true` is passed in the request.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const BulkUpdateCustomizationsRequest = z.object({
  customizations: z
    .array(
      z.object({
        customization: z
          .intersection(
            z.object({
              _id: z.string().describe('Customization ID.').min(1).max(36),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the customization is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the customization.\n\nIgnored when creating a customization.'
                ),
              _createdDate: z
                .date()
                .describe('Date and time the customization was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the customization was updated.')
                .optional()
                .nullable(),
              key: z
                .string()
                .describe(
                  'A read-only identifier generated from the customization name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                )
                .min(1)
                .max(50)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe(
                  'Customization name for options (for example, `"color"`, `"size"`) and modifiers (for example, `"greeting card"`).'
                )
                .min(1)
                .max(50),
              customizationType: z
                .enum(['PRODUCT_OPTION', 'MODIFIER'])
                .optional(),
              customizationRenderType: z
                .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
                .optional(),
              assignedProductsCount: z
                .number()
                .int()
                .describe(
                  'Number of products this customization is assigned to.\n> **Note:** Returned only when you pass `"ASSIGNED_PRODUCTS_COUNT"` to the `fields` array in Customizations API requests.'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                freeTextInput: z.never().optional(),
                choicesSettings: z.never().optional(),
              }),
              z.object({
                choicesSettings: z.never().optional(),
                freeTextInput: z
                  .object({
                    minCharCount: z
                      .number()
                      .int()
                      .describe('Minimum text character length.')
                      .optional(),
                    maxCharCount: z
                      .number()
                      .int()
                      .describe('Maximum text character length.')
                      .max(500)
                      .optional(),
                    defaultAddedPrice: z
                      .string()
                      .describe(
                        "Default amount added to a product's price when this choice is assigned to a modifier."
                      )
                      .optional()
                      .nullable(),
                    title: z
                      .string()
                      .describe(
                        'Title to display to customer for their free-text input.'
                      )
                      .min(1)
                      .max(100)
                      .optional(),
                    key: z
                      .string()
                      .describe(
                        'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                      )
                      .min(1)
                      .max(150)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Free text input settings.\n\n> **Note:** To be passed along with `customizationRenderType: FREE_TEXT`.'
                  ),
              }),
              z.object({
                freeTextInput: z.never().optional(),
                choicesSettings: z
                  .object({
                    choices: z
                      .array(
                        z.intersection(
                          z.object({
                            _id: z
                              .string()
                              .describe('Choice ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            choiceType: z
                              .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                              .optional(),
                            key: z
                              .string()
                              .describe(
                                'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                              )
                              .min(1)
                              .max(50)
                              .optional()
                              .nullable(),
                            name: z
                              .string()
                              .describe('Choice name.')
                              .min(1)
                              .max(50)
                              .optional(),
                            defaultAddedPrice: z
                              .string()
                              .describe(
                                "Default amount added to a product's price when this customization is assigned to a modifier."
                              )
                              .optional()
                              .nullable(),
                          }),
                          z.xor([
                            z.object({ colorCode: z.never().optional() }),
                            z.object({
                              colorCode: z
                                .string()
                                .describe(
                                  'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                )
                                .min(3)
                                .max(20),
                            }),
                          ])
                        )
                      )
                      .min(1)
                      .max(200)
                      .optional(),
                  })
                  .describe(
                    'Choices settings.\n\n> **Note:** Must be passed along with `customizationRenderType` of `SWATCH_CHOICES` and `TEXT_CHOICES`.'
                  ),
              }),
            ])
          )
          .describe('Customization to update.')
          .optional(),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether to return the full customization entities in the response.\n\nDefault: `false`'
        )
        .optional(),
      fields: z
        .array(z.enum(['ASSIGNED_PRODUCTS_COUNT']))
        .max(100)
        .optional(),
    })
    .optional(),
});
export const BulkUpdateCustomizationsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Bulk action metadata for customization.')
          .optional(),
        customization: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Customization ID.')
                .min(1)
                .max(36)
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the customization is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the customization.\n\nIgnored when creating a customization.'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe('Date and time the customization was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the customization was updated.')
                .optional()
                .nullable(),
              key: z
                .string()
                .describe(
                  'A read-only identifier generated from the customization name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                )
                .min(1)
                .max(50)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe(
                  'Customization name for options (for example, `"color"`, `"size"`) and modifiers (for example, `"greeting card"`).'
                )
                .min(1)
                .max(50)
                .optional(),
              customizationType: z
                .enum(['PRODUCT_OPTION', 'MODIFIER'])
                .describe('Customization type.')
                .optional(),
              customizationRenderType: z
                .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
                .describe(
                  'Customization render type.\n\nDefines how the customization will be displayed in the storefront.'
                )
                .optional(),
              assignedProductsCount: z
                .number()
                .int()
                .describe(
                  'Number of products this customization is assigned to.\n> **Note:** Returned only when you pass `"ASSIGNED_PRODUCTS_COUNT"` to the `fields` array in Customizations API requests.'
                )
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                freeTextInput: z.never().optional(),
                choicesSettings: z.never().optional(),
              }),
              z.object({
                choicesSettings: z.never().optional(),
                freeTextInput: z
                  .object({
                    minCharCount: z
                      .number()
                      .int()
                      .describe('Minimum text character length.')
                      .optional(),
                    maxCharCount: z
                      .number()
                      .int()
                      .describe('Maximum text character length.')
                      .max(500)
                      .optional(),
                    defaultAddedPrice: z
                      .string()
                      .describe(
                        "Default amount added to a product's price when this choice is assigned to a modifier."
                      )
                      .optional()
                      .nullable(),
                    title: z
                      .string()
                      .describe(
                        'Title to display to customer for their free-text input.'
                      )
                      .min(1)
                      .max(100)
                      .optional(),
                    key: z
                      .string()
                      .describe(
                        'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                      )
                      .min(1)
                      .max(150)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Free text input settings.\n\n> **Note:** To be passed along with `customizationRenderType: FREE_TEXT`.'
                  ),
              }),
              z.object({
                freeTextInput: z.never().optional(),
                choicesSettings: z
                  .object({
                    choices: z
                      .array(
                        z.intersection(
                          z.object({
                            _id: z
                              .string()
                              .describe('Choice ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            choiceType: z
                              .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                              .describe(
                                'Choice type.\n\n> **Notes:**\n> + For `customizationRenderType: SWATCH_CHOICES`, the supported `choiceType` value is: `ONE_COLOR`.\n> + For a `customizationRenderType` of `TEXT_CHOICES`, the supported `choiceType` value is: `CHOICE_TEXT`.'
                              )
                              .optional(),
                            key: z
                              .string()
                              .describe(
                                'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                              )
                              .min(1)
                              .max(50)
                              .optional()
                              .nullable(),
                            name: z
                              .string()
                              .describe('Choice name.')
                              .min(1)
                              .max(50)
                              .optional(),
                            defaultAddedPrice: z
                              .string()
                              .describe(
                                "Default amount added to a product's price when this customization is assigned to a modifier."
                              )
                              .optional()
                              .nullable(),
                          }),
                          z.xor([
                            z.object({ colorCode: z.never().optional() }),
                            z.object({
                              colorCode: z
                                .string()
                                .describe(
                                  'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                )
                                .min(3)
                                .max(20),
                            }),
                          ])
                        )
                      )
                      .min(1)
                      .max(200)
                      .optional(),
                  })
                  .describe(
                    'Choices settings.\n\n> **Note:** Must be passed along with `customizationRenderType` of `SWATCH_CHOICES` and `TEXT_CHOICES`.'
                  ),
              }),
            ])
          )
          .describe(
            'Full customization entity.\n\nReturned only if `returnEntity: true` is passed in the request.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
